
import {
  computed, PropType, defineComponent, Ref, watch, toRefs, shallowRef,
} from '@vue/composition-api';
import {
  useRoute, useRouter,
} from 'vue2-helpers/vue-router';
import { getCategories } from '@/lib/utils';
import {
  OS, Arch, Extension, hasExtensionManagerModel, listExtensions,
} from '@/lib/api/extension.service';

import Bus from '@/plugins/bus';
import AppBar from '@/components/AppBar.vue';
import ExtensionCard from '@/components/ExtensionCard.vue';
import CategoryList from '@/components/CategoryList.vue';

const AppId = process.env.VUE_APP_APP_ID as string;

export default defineComponent({
  name: 'CatalogView',

  props: {
    category: {
      type: String as PropType<string>,
      required: true,
    },
    revision: {
      type: String as PropType<string>,
      required: true,
    },
    os: {
      type: String as PropType<OS>,
      required: true,
    },
    arch: {
      type: String as PropType<Arch | undefined>,
      default: undefined,
    },
    legacy: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },

  components: {
    AppBar,
    CategoryList,
    ExtensionCard,
  },

  setup(props) {
    const route = useRoute();
    const router = useRouter();

    const selectPlatform = ({ os, arch }: { os: OS; arch: Arch }): void => {
      const { query } = route;
      if (props.legacy) {
        router.push({ name: 'Catalog Legacy', query: { ...query, os, arch } }).catch((error: Error) => {
          if (error.name !== 'NavigationDuplicated') {
            throw error;
          }
        });
        return;
      }
      const location = { name: 'Catalog', params: { os, arch }, query };
      router.push(location).catch((error: Error) => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    };

    const query = computed({
      get(): string {
        return (props.legacy ? route.query.search : route.query.q || '') as string;
      },
      set(q: string): void {
        if (props.legacy) {
          router.push({ name: 'Catalog Legacy', query: { ...route.query, search: q } }).catch((error: Error) => {
            if (error.name !== 'NavigationDuplicated') {
              throw error;
            }
          });
          return;
        }
        router.replace({ name: 'Catalog', params: { arch: route.params.arch }, query: { q } }).catch((error: Error) => {
          if (error.name !== 'NavigationDuplicated') {
            throw error;
          }
        });
      },
    });

    const propsRefs = toRefs(props);
    const extensions = shallowRef([]) as Ref<Extension[]>;

    const loadExtensions = async () => {
      const params = {
        appId: AppId,
        revision: parseInt(props.revision, 10),
        os: props.os,
        arch: props.arch,
        query: query.value,
      };
      const { data } = await listExtensions(params);
      extensions.value = data;
    };

    loadExtensions();

    Bus.$on('extension-state-updated', () => loadExtensions());

    watch([
      propsRefs.revision,
      propsRefs.os,
      propsRefs.arch,
      query,
    ], loadExtensions);

    const categories = computed(() => ((
      [['All', extensions.value.length]] as [string, number][]).concat(getCategories(extensions.value))));

    const filteredExtensions = computed((): Extension[] => {
      const allExtensions = extensions.value;
      if (props.category.toLowerCase() !== 'all') {
        return allExtensions.filter((e) => e.meta.category === props.category);
      }
      return extensions.value;
    });

    const windowHasExtensionManagerModel = hasExtensionManagerModel();

    const valid = computed(() => {
      if (!(props.os in OS)) {
        return `Invalid OS.  Choose one of ${Object.keys(OS)}`;
      }
      if (props.arch !== undefined && !(props.arch in Arch)) {
        return `Invalid architecutre.  Choose one of ${Object.keys(Arch)}`;
      }
      return '';
    });

    return {
      query,
      extensions,
      categories,
      filteredExtensions,
      windowHasExtensionManagerModel,
      valid,
      selectPlatform,
    };
  },
});
